import * as THREE from 'three';
import { Entity } from './Entity.js';
import { CONFIG } from '../config.js';

/**
 * @typedef {Object} LegJoint
 * @property {THREE.Group} upper - Upper leg joint group
 * @property {THREE.Group} lower - Lower leg joint group
 */

/**
 * @typedef {Object} CatOptions
 * @property {THREE.Vector3} [position] - Initial position
 * @property {number} [rotation] - Initial rotation in radians
 */

/**
 * The player-controlled cat entity.
 * Features a detailed mesh with body bobbing, leg animation, and tail wagging.
 */
export class Cat extends Entity {
    /** @type {THREE.Group} */
    #catBody;

    /** @type {THREE.Group} */
    #tailGroup;

    /** @type {THREE.Group[]} */
    #tailSegments = [];

    /** @type {LegJoint[]} */
    #legJoints = [];

    /** @type {number} */
    #walkCycle = 0;

    /** @type {boolean} */
    #isMoving = false;

    /** @type {boolean} */
    #isRunning = false;

    /**
     * @param {CatOptions} [options]
     */
    constructor(options = {}) {
        super({
            position: options.position ?? new THREE.Vector3(0, CONFIG.cat.groundHeight, 0),
            rotation: options.rotation ?? 0,
            collisionRadius: CONFIG.cat.collisionRadius,
            collidable: true,
        });

        this.#buildMesh();
    }

    /**
     * Set movement state for animation
     * @param {boolean} moving - Whether the cat is moving
     * @param {boolean} running - Whether the cat is running
     */
    setMovementState(moving, running) {
        this.#isMoving = moving;
        this.#isRunning = running;
    }

    /**
     * Current vertical body-bob offset used by locomotion animation.
     * Attachments mounted to the cat root can use this to stay in sync.
     * @returns {number}
     */
    get bodyBobOffsetY() {
        return this.#catBody.position.y;
    }

    /**
     * Build the complete cat mesh with all body parts
     */
    #buildMesh() {
        // Main body container (bobs during walk)
        this.#catBody = new THREE.Group();
        this.mesh.add(this.#catBody);

        // Body (capsule)
        const bodyGeometry = new THREE.CapsuleGeometry(0.5, 1, 4, 8);
        const bodyMaterial = new THREE.MeshStandardMaterial({ color: 0xFFA500 }); // Orange cat
        const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
        body.rotation.z = Math.PI / 2; // Horizontal
        body.rotation.y = Math.PI / 2; // Face forward
        body.castShadow = true;
        this.#catBody.add(body);

        // Head
        this.#addHead();

        // Ears
        this.#addEars();

        // Legs
        this.#addLegs();

        // Tail
        this.#addTail();
    }

    /**
     * Add the cat's head with eyes, nose, and mouth
     */
    #addHead() {
        const headGeometry = new THREE.SphereGeometry(0.4, 16, 16);
        const headMaterial = new THREE.MeshStandardMaterial({ color: 0xFFA500 });
        const head = new THREE.Mesh(headGeometry, headMaterial);
        head.position.set(0, 0.2, -0.8);
        head.castShadow = true;
        this.#catBody.add(head);

        // Eyes
        const eyeGeometry = new THREE.SphereGeometry(0.06, 8, 8);
        const eyeMaterial = new THREE.MeshStandardMaterial({ color: 0x000000 });

        const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        leftEye.position.set(0.15, 0.25, -0.3);
        head.add(leftEye);

        const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        rightEye.position.set(-0.15, 0.25, -0.3);
        head.add(rightEye);

        // Nose
        const noseGeometry = new THREE.ConeGeometry(0.08, 0.08, 4);
        const noseMaterial = new THREE.MeshStandardMaterial({ color: 0xFFA0A0 });
        const nose = new THREE.Mesh(noseGeometry, noseMaterial);
        nose.rotation.x = Math.PI / 2;
        nose.position.set(0, 0.1, -0.4);
        head.add(nose);

        // Mouth (curved line made of small boxes)
        const mouthGeometry = new THREE.BoxGeometry(0.03, 0.03, 0.03);
        const mouthMaterial = new THREE.MeshStandardMaterial({ color: 0x000000 });

        for (let i = 0; i < 5; i++) {
            const mouthPart = new THREE.Mesh(mouthGeometry, mouthMaterial);
            const angle = (i - 2) * 0.2;
            mouthPart.position.set(
                Math.sin(angle) * 0.1,
                -0.1 + Math.abs(angle) * 0.05,
                -0.15
            );
            head.add(mouthPart);
        }
    }

    /**
     * Add the cat's ears
     */
    #addEars() {
        const earGeometry = new THREE.ConeGeometry(0.2, 0.3, 4);
        const earMaterial = new THREE.MeshStandardMaterial({ color: 0xFFA500 });

        const leftEar = new THREE.Mesh(earGeometry, earMaterial);
        leftEar.position.set(0.2, 0.6, -0.8);
        leftEar.castShadow = true;
        this.#catBody.add(leftEar);

        const rightEar = new THREE.Mesh(earGeometry, earMaterial);
        rightEar.position.set(-0.2, 0.6, -0.8);
        rightEar.castShadow = true;
        this.#catBody.add(rightEar);
    }

    /**
     * Add the cat's legs with joints
     */
    #addLegs() {
        const legSegmentLength = 0.35;
        const legRadius = 0.05;

        const upperLegGeometry = new THREE.CylinderGeometry(legRadius, legRadius, legSegmentLength);
        const lowerLegGeometry = new THREE.CylinderGeometry(legRadius, legRadius, legSegmentLength);
        const legMaterial = new THREE.MeshStandardMaterial({ color: 0xFFA500 });

        const legPositions = [
            [0.3, -0.25, -0.3],  // Front right
            [-0.3, -0.25, -0.3], // Front left
            [0.3, -0.25, 0.3],   // Back right
            [-0.3, -0.25, 0.3],  // Back left
        ];

        legPositions.forEach((pos) => {
            const legJoint = new THREE.Group();
            legJoint.position.set(pos[0], pos[1], pos[2]);
            this.#catBody.add(legJoint);

            // Upper leg
            const upperLeg = new THREE.Mesh(upperLegGeometry, legMaterial);
            upperLeg.position.y = -legSegmentLength / 2;
            upperLeg.castShadow = true;
            legJoint.add(upperLeg);

            // Lower leg joint
            const lowerJoint = new THREE.Group();
            lowerJoint.position.y = -legSegmentLength / 2;
            upperLeg.add(lowerJoint);

            // Lower leg
            const lowerLeg = new THREE.Mesh(lowerLegGeometry, legMaterial);
            lowerLeg.position.y = -legSegmentLength / 2;
            lowerLeg.castShadow = true;
            lowerJoint.add(lowerLeg);

            this.#legJoints.push({ upper: legJoint, lower: lowerJoint });
        });
    }

    /**
     * Add the cat's multi-segmented tail
     */
    #addTail() {
        this.#tailGroup = new THREE.Group();
        this.#tailGroup.position.set(0, 0.2, 0.8);
        this.mesh.add(this.#tailGroup);

        const tailSegmentCount = 10;
        const tailLength = 1.3;
        const segmentLength = tailLength / tailSegmentCount;
        const baseRadius = 0.15;

        // Create first segment
        const firstSegmentGroup = new THREE.Group();
        this.#tailGroup.add(firstSegmentGroup);
        this.#tailSegments.push(firstSegmentGroup);

        const firstSegmentGeo = new THREE.CylinderGeometry(
            baseRadius * 0.9,
            baseRadius,
            segmentLength,
            8
        );
        const tailMaterial = new THREE.MeshStandardMaterial({ color: 0xFFA500 });
        const firstSegment = new THREE.Mesh(firstSegmentGeo, tailMaterial);
        firstSegment.rotation.z = Math.PI / 2;
        firstSegment.position.z = segmentLength / 2;
        firstSegment.rotation.y = Math.PI / 2;
        firstSegment.castShadow = true;
        firstSegmentGroup.add(firstSegment);

        // Create remaining segments
        for (let i = 1; i < tailSegmentCount; i++) {
            const segmentGroup = new THREE.Group();
            const radius = baseRadius * (1 - (i / tailSegmentCount) * 0.7);
            const tailGeometry = new THREE.CylinderGeometry(
                radius * 0.8,
                radius,
                segmentLength + 0.03,
                8
            );
            const segment = new THREE.Mesh(tailGeometry, tailMaterial);
            segment.rotation.z = Math.PI / 2;
            segment.position.z = segmentLength / 2;
            segment.rotation.y = Math.PI / 2;
            segment.castShadow = true;
            segmentGroup.add(segment);

            segmentGroup.position.z = segmentLength;
            this.#tailSegments[i - 1].add(segmentGroup);
            this.#tailSegments.push(segmentGroup);
        }
    }

    /**
     * Update cat animations
     * @param {number} delta - Time since last frame in seconds
     */
    update(delta) {
        this.#animateWalk(delta);
        this.#animateTail(delta);
    }

    /**
     * Animate walking legs and body bobbing
     * @param {number} delta - Time since last frame in seconds
     */
    #animateWalk(delta) {
        if (this.#isMoving) {
            const cycleSpeed = this.#isRunning ? 15 : 10;
            this.#walkCycle += delta * cycleSpeed;

            this.#legJoints.forEach((joint, index) => {
                const legOffset = index % 2 === 0 ? 0 : Math.PI;
                const frontBackOffset = index < 2 ? 0 : Math.PI;
                const cycle = this.#walkCycle + legOffset + frontBackOffset;

                // Upper leg swing
                const upperSwing = Math.sin(cycle) * 0.4;
                joint.upper.rotation.x = upperSwing;

                // Lower leg adjusts opposite
                const lowerSwing = Math.sin(cycle + Math.PI / 2) * 0.3;
                joint.lower.rotation.x = lowerSwing;
            });

            // Body bobbing
            const bobAmount = this.#isRunning ? 0.07 : 0.05;
            this.#catBody.position.y = Math.sin(this.#walkCycle * 2) * bobAmount;
        } else {
            // Reset leg positions when not moving
            this.#legJoints.forEach(joint => {
                joint.upper.rotation.x = 0;
                joint.lower.rotation.x = 0;
            });
            this.#catBody.position.y = 0;
        }
    }

    /**
     * Animate the tail wagging with wave motion
     * @param {number} delta - Time since last frame in seconds
     */
    #animateTail(delta) {
        // Base tail angle
        const baseAngle = Math.sin(Date.now() * 0.002) * 0.3;
        this.#tailGroup.rotation.y = baseAngle;

        // Sync tail position with body bobbing
        this.#tailGroup.position.y = 0.2 + this.#catBody.position.y;

        // Wave motion through segments
        this.#tailSegments.forEach((segment, index) => {
            if (index > 0) {
                const phaseShift = index * 0.3;
                const waveIntensity = 0.15 + (index * 0.05);

                // Side to side
                segment.rotation.y = Math.sin(Date.now() * 0.002 - phaseShift) * waveIntensity;

                // Subtle up/down
                segment.rotation.x = Math.sin(Date.now() * 0.003 - phaseShift) * 0.1;
            }
        });
    }
}
